/**
 * @fileoverview The Monday garden email's links: /email/monday?t=TOKEN&do=monthly&lang=es.
 *
 * Every Monday email has "Send me this monthly instead" (or "…weekly
 * instead", once monthly) and "Unsubscribe". Both open this page. It needs
 * no sign-in (the token, sent only to that gardener, is what the backend
 * trusts) and opens in the language the email was in. It asks once, with
 * one button, rather than change anything on opening: mail scanners open
 * links by themselves. (A mail app's own one-click unsubscribe goes straight
 * to the backend, from the email's headers, and never comes here.)
 *
 * Done, it says what changed, that warning emails are not touched, and how
 * to change it again (the profile's Monday garden email row).
 *
 * Public, so outside the Layout, like the reminder's one-tap cancel.
 *
 * Where: the route /email/monday?t=&do=weekly|monthly|off&lang= (App.tsx),
 * public.
 * API: GET /garden-email/link?t= (readMondayLink) on opening; POST
 * /garden-email/link { token, frequency } (confirmMondayLink) on the button.
 * Settings: ?do= picks the choice (anything unknown is 'off'); ?lang= sets
 * the app's language.
 * Words (en/es): email.monday.title, .loading, .unknown, .failed,
 * .toProfile, .ask.<choice>, .button.<choice>, .done.<choice>. A failure on
 * our side (a 500, no connection; D31, lib/serverFailure) says the standard
 * sentence, reported to Sentry, with the support address as a link, in place
 * of email.monday.failed (lib/errorMessage).
 * Watch out: a link already at what it asks for opens straight on "done",
 * with no button.
 *
 * @module MondayEmailLinkPage
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { confirmMondayLink, isMondayFrequency, readMondayLink, type MondayEmailLink, type MondayFrequency } from '@/shared/api/gardenEmail';

/** What the page shows: reading the link, asking, saving, done, or a failure with what was thrown. */
type Shown =
  | { kind: 'loading' }
  | { kind: 'ask'; link: MondayEmailLink }
  | { kind: 'working'; link: MondayEmailLink }
  | { kind: 'done'; link: MondayEmailLink }
  | { kind: 'failed'; link: MondayEmailLink | null; error: unknown };

/** The open page the Monday email's links go to. */
export const MondayEmailLinkPage: React.FC = () => {
  const [params] = useSearchParams();
  const { reactTranslate: t, i18n } = useTranslation();
  const token = params.get('t') ?? '';
  const asked = params.get('do');
  // What the link asks for; anything else is read as unsubscribing, the one choice a link must never get wrong the other way.
  const wanted: MondayFrequency = isMondayFrequency(asked) ? asked : 'off';
  // The email's language, set before the first words are drawn.
  React.useState(() => {
    const language = params.get('lang');
    if ((language === 'en' || language === 'es') && language !== i18n.language) i18n.changeLanguage(language);
    return language;
  });
  const [shown, setShown] = React.useState<Shown>({ kind: 'loading' });

  React.useEffect(() => {
    let cancelled = false;
    readMondayLink(token)
      .then((link) => {
        if (cancelled) return;
        // Already as asked: nothing to ask.
        setShown({ kind: link.status === 'ready' && link.frequency === wanted ? 'done' : 'ask', link });
      })
      .catch((error) => { if (!cancelled) setShown({ kind: 'failed', link: null, error }); });
    return () => { cancelled = true; };
  }, [token, wanted]);

  /**
   * The one button: sets the choice the link asked for.
   *
   * @param link - The link as the server read it.
   */
  const confirm = async (link: MondayEmailLink): Promise<void> => {
    setShown({ kind: 'working', link });
    try {
      const after = await confirmMondayLink(token, wanted);
      setShown({ kind: after.status === 'ready' ? 'done' : 'ask', link: after });
    } catch (error) {
      setShown({ kind: 'failed', link, error });
    }
  };

  const link = shown.kind === 'loading' ? null : shown.link;
  const unknown = link?.status === 'unknown';

  return (
    <div className="min-h-screen w-full bg-background px-4 py-12">
      <div className="mx-auto max-w-md space-y-5 text-foreground">
        <p className="text-2xl font-semibold text-primary font-grow">Grow</p>
        <h1 className="text-xl font-semibold font-body">{t('email.monday.title')}</h1>

        {shown.kind === 'loading' ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground"><Spinner size={18} />{t('email.monday.loading')}</div>
        ) : unknown ? (
          <p className="text-sm leading-relaxed">{t('email.monday.unknown')}</p>
        ) : shown.kind === 'done' ? (
          <p role="status" className="text-sm leading-relaxed">{t(`email.monday.done.${wanted}`)}</p>
        ) : (
          <>
            <p className="text-sm leading-relaxed">{t(`email.monday.ask.${wanted}`)}</p>
            {shown.kind === 'failed' && <p role="alert" className="text-sm text-destructive"><FailureText text={errorMessage(shown.error, t('email.monday.failed'))} /></p>}
            {link && (
              <Button onClick={() => { void confirm(link); }} disabled={shown.kind === 'working'}>
                {shown.kind === 'working' ? <Spinner size={16} color="currentColor" /> : null}
                <span className={shown.kind === 'working' ? 'ml-2' : ''}>{t(`email.monday.button.${wanted}`)}</span>
              </Button>
            )}
          </>
        )}

        <p className="pt-4 text-sm">
          <a href="/profile?s=monday" className="text-primary underline underline-offset-2">{t('email.monday.toProfile')}</a>
        </p>
      </div>
    </div>
  );
};

export default MondayEmailLinkPage;
